import { useState } from "react";

import { useNotificationRulesQuery, useDeleteNotificationChannelTopicMutation } from "./NotificationRules.g";

import { Box } from "@design-system/Box/Box";
import { Button } from "@design-system/Button/Button";
import { Table } from "@design-system/Table/Table";
import { Text } from "@design-system/Text/Text";
import { IconButton } from "@design-system/IconButton/IconButton";
import { Tag } from "@design-system/Tag/Tag";
import { DateCell } from "@design-system/Table/UI/DateCell/DateCell";

import { PortalHeaderActions } from "@layouts/PortalPage/PortalHeaderActions";
import { RuleSlideInPanel } from "./RuleSlideInPanel/RuleSlideInPanel";

export const NotificationRules = () => {
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [selectedRuleId, setSelectedRuleId] = useState<string | undefined>();
  const [pageIndex, setPageIndex] = useState(0);

  const { data, refetch } = useNotificationRulesQuery({
    first: 50,
  });

  const { mutateAsync: deleteRule } = useDeleteNotificationChannelTopicMutation();

  const rules = data?.notificationChannelTopics?.edges?.filter(e => e !== null).map((e) => e!.node) || [];
  const totalCount = data?.notificationChannelTopics?.totalCount || 0;

  const handleCreateRule = () => {
    setSelectedRuleId(undefined);
    setIsPanelOpen(true);
  };

  const handleEditRule = (id: string) => {
    setSelectedRuleId(id);
    setIsPanelOpen(true);
  };

  const handleDeleteRule = async (id: string) => {
    if (confirm("Are you sure you want to delete this rule?")) {
      await deleteRule({ input: { id } });
      refetch();
    }
  };

  const handlePanelClose = () => {
    setIsPanelOpen(false);
    setSelectedRuleId(undefined);
  };

  const handlePanelSuccess = () => {
    handlePanelClose();
    refetch();
  };

  const columns = [
    {
      id: "name",
      header: "Rule Name",
      accessorKey: "name",
      cell: (info: { getValue: () => unknown }) => <Text weight={500}>{info.getValue() as string}</Text>,
    },
    {
      id: "status",
      header: "Status",
      accessorKey: "notificationChannel.isEnabled",
      cell: (info: { getValue: () => unknown }) => {
        const isEnabled = info.getValue() as boolean;
        return <Tag color={isEnabled ? "success" : "secondary"} text={isEnabled ? "Active" : "Disabled"} />;
      },
    },
    {
      id: "channel",
      header: "Channel",
      accessorKey: "notificationChannel.channelType",
      cell: (info: { getValue: () => unknown }) => <Tag color="secondary" text={info.getValue() as string} />,
    },
    {
      id: "events",
      header: "Events",
      accessorKey: "types",
      cell: (info: { getValue: () => unknown }) => {
        const types = (info.getValue() as Array<{ id: string; notificationType: string }>) || [];
        if (types.length === 0) return <Text color="textSecondary">All</Text>;
        return (
          <Box direction="row" gap={4} wrap>
            {types.map((t) => (
              <Tag key={t.id} color="primary" text={t.notificationType} />
            ))}
          </Box>
        );
      },
    },
    {
      id: "applicationScopes",
      header: "App Scopes",
      accessorKey: "applicationReviewStates",
      cell: (info: { getValue: () => unknown }) => {
        const states = (info.getValue() as string[]) || [];
        if (states.length === 0) return <Text color="textSecondary">All</Text>;
        return (
          <Box direction="row" gap={4} wrap>
            {states.map((state) => (
              <Tag key={state} color="secondary" text={state} />
            ))}
          </Box>
        );
      },
    },
    {
      id: "clients",
      header: "Clients",
      accessorKey: "clients",
      cell: (info: { getValue: () => unknown }) => {
        const clients = (info.getValue() as Array<unknown>) || [];
        if (clients.length === 0) return <Text color="textSecondary">All</Text>;
        return <Text>{clients.length} selected</Text>;
      },
    },
    {
      id: "recipients",
      header: "Recipients",
      accessorKey: "emailRecipients",
      cell: (info: { getValue: () => unknown }) => {
        const recipients = (info.getValue() as Array<unknown>) || [];
        return <Text>{recipients.length} addresses</Text>;
      },
    },
    {
      id: "createdAt",
      header: "Created",
      accessorKey: "createdAt",
      cell: (info: { getValue: () => unknown }) => {
        const val = info.getValue() as string;
        return val ? <DateCell date={new Date(val)} /> : null;
      },
    },
    {
      id: "actions",
      header: "",
      accessorKey: "id",
      cell: (info: { getValue: () => unknown }) => {
        const id = info.getValue() as string;
        return (
          <Box direction="row" gap={8} justify="flex-end">
            <IconButton icon="faPencil" onClick={() => handleEditRule(id)} size="sm" />
            <IconButton icon="faTrash" onClick={() => handleDeleteRule(id)} size="sm" />
          </Box>
        );
      },
    },
  ];

  return (
    <>
      <PortalHeaderActions>
        <Button onClick={handleCreateRule} size="sm">
          Create Rule
        </Button>
      </PortalHeaderActions>

      {rules.length === 0 ? (
        <Box padding="48px" align="center" justify="center" direction="column" gap={16} fullStretch>
          <Text color="textSecondary">No notification rules configured yet.</Text>
          <Button onClick={handleCreateRule} variant="outlined" size="sm">Create your first rule</Button>
        </Box>
      ) : (
        <Table
          data={rules}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          columns={columns as any}
          rowCount={totalCount}
          pagination={{ pageIndex, pageSize: 50 }}
          onPaginationChange={(updater) => {
            if (typeof updater === 'function') {
              const nextState = updater({ pageIndex, pageSize: 50 });
              setPageIndex(nextState.pageIndex);
            } else {
              setPageIndex(updater.pageIndex);
            }
          }}
          sorting={[]}
          onSortingChange={() => {}}
          onPageSizeChange={() => {}}
        />
      )}

      {isPanelOpen && (
        <RuleSlideInPanel
          isOpen={isPanelOpen}
          ruleId={selectedRuleId}
          onClose={handlePanelClose}
          onSuccess={handlePanelSuccess}
        />
      )}
    </>
  );
};
